/**
 * @file index.tsx
 *
 * @fileoverview Renders blogs search pages.
 */

import { GetServerSideProps, NextPage } from 'next';
import initApollo from '~/jobiqo/apollo/initApollo';
import BlogSearch, { BlogSearchProps } from '~/jobiqo/components/BlogSearch';
import { getBlogPostsVariables } from '~/jobiqo/components/BlogSearch/utils';
import { getCleanUrlPart } from '~/jobiqo/components/Search/utils/searchHelpers';
import GET_BLOG_CATEGORIES from '~/jobiqo/pages/blogs/graphql/queries/getBlogCategories.gql';
import GET_BLOG_POSTS from '~/jobiqo/pages/blogs/graphql/queries/getBlogPosts.gql';
import GET_BLOG_TAGS from '~/jobiqo/pages/blogs/graphql/queries/getBlogTags.gql';
import GET_FEATURED_BLOGS from '~/jobiqo/pages/blogs/graphql/queries/getFeaturedBlogs.gql';
import GET_POPULAR_BLOG_TAGS from '~/jobiqo/pages/blogs/graphql/queries/getPopularBlogTags.gql';
import ROUTER_QUERY from '~/jobiqo/pages/blogs/graphql/queries/getBlogCategoryURL.gql';
import { removeNextJsQueryParams } from '~/jobiqo/utils/routes';
import { isRedirectCode, isPermanentRedirectCode } from '~/jobiqo/lib/redirect';
import { getServerFrontendConfig } from '~/jobiqo/lib/frontendConfig';
export const BLOGS_PER_PAGE = 9;
const POPULAR_TAGS_LIMIT = 8;

const BlogsPage: NextPage<BlogSearchProps> = (props) => (
  <BlogSearch {...props} />
);

export const getServerSideProps: GetServerSideProps = async function (ctx) {
  const frontendConfig = getServerFrontendConfig();

  if (!frontendConfig.APP.BLOG_FEATURE_ACTIVE) {
    return {
      props: {},
      redirect: {
        destination: '/404',
      },
    };
  }

  const apolloClient = initApollo({}, ctx);
  const {
    locale,
    query: { search = '', page = '' },
  } = ctx;
  const cleanUrl = getCleanUrlPart(ctx.resolvedUrl);

  const { data: redirectData } = await apolloClient.query({
    query: ROUTER_QUERY,
    variables: {
      path: cleanUrl,
      language: ctx.locale,
    },
  });

  if (
    isRedirectCode(redirectData?.route?.code) &&
    cleanUrl !== redirectData?.route?.target
  ) {
    // If we get a redirect response redirect to the target.
    return {
      redirect: {
        destination: redirectData.route.target,
        permanent: isPermanentRedirectCode(redirectData?.route?.code),
      },
      props: {},
    };
  }

  const { data: categoriesData } = await apolloClient.query({
    query: GET_BLOG_CATEGORIES,
    variables: {
      locale,
    },
  });

  const currentCategory = categoriesData?.categories.find((c) => {
    return c.url?.path === cleanUrl || c.url?.path === `/${locale}${cleanUrl}`;
  });

  // Request to an unexisting category.
  if (ctx.query.category && !currentCategory) {
    return {
      props: {},
      redirect: {
        destination: '/404',
      },
    };
  }

  const { data: tagsData } = await apolloClient.query({
    query: GET_BLOG_TAGS,
    variables: {
      locale,
    },
  });

  const currentTag = tagsData?.tags.find((c) => {
    const cleanUrl = getCleanUrlPart(ctx.resolvedUrl);
    return c.url?.path === cleanUrl || c.url?.path === `/${locale}${cleanUrl}`;
  });

  // Request to an unexisting tag.
  if (ctx.query.tagName && !currentTag) {
    return {
      props: {},
      redirect: {
        destination: '/404',
      },
    };
  }

  const { data: blogsData, error, loading } = await apolloClient.query({
    query: GET_BLOG_POSTS,
    variables: getBlogPostsVariables(
      search,
      BLOGS_PER_PAGE,
      page,
      locale,
      currentCategory?.tid,
      currentTag?.tid,
      removeNextJsQueryParams(ctx.resolvedUrl),
    ),
  });

  const { data: popularBlogTags } = await apolloClient.query({
    query: GET_POPULAR_BLOG_TAGS,
    variables: {
      limit: POPULAR_TAGS_LIMIT,
    },
  });

  const { data: featuredBlogs } = await apolloClient.query({
    query: GET_FEATURED_BLOGS,
  });

  return {
    props: {
      blogSearchResult: blogsData.blogPosts,
      error: !!error,
      loading,
      categories: categoriesData?.categories ?? [],
      popularTags: popularBlogTags?.tags ?? [],
      featuredBlogs: featuredBlogs?.featuredBlogs[0].items ?? [],
      ...(currentCategory && { currentCategory }),
      ...(currentTag && { currentTag }),
    },
  };
};

export default BlogsPage;
